import * as React from 'react';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { IntlShape, useIntl } from 'react-intl';
import { RadioButton } from 'primereact/radiobutton';
import { GetSingleEntryRequest, PublistListApi } from '../../api/core';
import { getAuthMiddleware } from '../../Auth/utils/GetAuthMiddleware';
import { AuthContext } from '../../Auth/components/AuthProvider';
import { Button } from 'primereact/button';
import { htmlToText } from 'html-to-text';
import { ToastContext, ToastContextType } from '../../util/providers/ToastProvider';
import { citationStyles } from '../../Settings/citation-settings';

interface Props {
  id: number;
  visible: boolean;
  setVisible: (visible: boolean) => void;
}

export const CitationDialog = (props: Props): ReactNode => {
  const intl: IntlShape = useIntl();
  const {actions} = useContext(AuthContext);
  const toast: ToastContextType = useContext(ToastContext);
  const api: PublistListApi = new PublistListApi().withMiddleware(getAuthMiddleware(actions));
  const [citation, setCitation] = useState<string>(null);
  const [display, setDisplay] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect((): void => {
    if (!props.visible) return;
    if (!citation) setCitation(citationStyles[0].key);
  }, [citation, props.visible]);

  useEffect((): void => {
    if (!props.visible) return;
    if (props.id === null) return;
    if (!citation) return;

    setDisplay((): string => '');
    setLoading(true);

    const request: GetSingleEntryRequest = {workId: props.id, style: citation, locale: intl.locale};
    api.getSingleEntry(request)
      .then((value: string): void => setDisplay((): string => value))
      .catch((err: any): void => console.error(err))
      .finally((): void => setLoading(false));
  }, [citation, intl.locale, props.id, props.visible]);

  if (props.id === null || !props.visible) {
    return <></>;
  }

  return <Dialog header={intl.formatMessage({id: 'popup.citation.title'})}
                 visible={props.visible}
                 style={{width: '100%', maxWidth: '1000px'}}
                 closeOnEscape={true}
                 dismissableMask={true}
                 onHide={(): void => props.setVisible(false)}>
    <div className={'m-0 flex flex-column gap-4'}>
      <div className={'w-12 flex flex-wrap gap-3'}>
        {citationStyles.map(({key, value}: { key: string; value: string }): ReactNode => (
          <div key={key} className={'flex gap-1 align-items-center'}>
            <RadioButton disabled={loading} inputId={key} name={'citation'} value={key}
                         onChange={(): void => setCitation(key)} checked={citation === key}/>
            <label htmlFor={key} className={''}>{value}</label>
          </div>
        ))}
      </div>
      <div className={'w-12 h-10rem border-solid border-round border-1 border-200 p-3 overflow-y-auto'}
           dangerouslySetInnerHTML={{__html: display}}/>
      <div className={'flex gap-4'}>
        <Button
          label={intl.formatMessage({id: 'copy-clipboard.label'})}
          disabled={loading || !display}
          onClick={(): Promise<void> =>
            navigator.clipboard.writeText(htmlToText(display))
              .then((): void => {
                toast.notify({
                  summary: intl.formatMessage({id: 'copy-clipboard.confirm'}),
                  severity: 'info',
                });
              })
          }
        />
      </div>
    </div>
  </Dialog>;
};
